import React, { useState, useEffect } from 'react';
import { X, Briefcase, Edit, ChevronDown } from 'lucide-react';
import './AddProjectModal.css';

import api from '../../../services/api';

const parseValueAndCurrency = (valueStr) => {
    if (!valueStr) return { currency: 'AED', amount: '' };
    const str = String(valueStr).trim();
    const match = str.match(/^(AED|USD|EUR|SAR|IND|INR)\s*(.*)$/i);
    if (match) {
        return {
            currency: match[1].toUpperCase() === 'INR' ? 'IND' : match[1].toUpperCase(),
            amount: match[2].trim()
        };
    }
    return { currency: 'AED', amount: str };
};

const formatIndian = (numStr) => {
    if (!numStr) return '';
    let lastThree = numStr.substring(numStr.length - 3);
    let otherDigits = numStr.substring(0, numStr.length - 3);
    if (otherDigits !== '') {
        lastThree = ',' + lastThree;
    }
    return otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
};

const formatInternational = (numStr) => {
    if (!numStr) return '';
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatValuation = (value, curr) => {
    if (value === undefined || value === null) return '';
    const rawVal = String(value).replace(/[^0-9.]/g, '');
    const dotIndex = rawVal.indexOf('.');
    let integerPart = rawVal;
    let decimalPart = '';
    if (dotIndex !== -1) {
        integerPart = rawVal.slice(0, dotIndex);
        decimalPart = rawVal.slice(dotIndex + 1);
    }

    let formattedInteger = '';
    if (curr === 'IND') {
        formattedInteger = formatIndian(integerPart);
    } else {
        formattedInteger = formatInternational(integerPart);
    }

    let result = formattedInteger;
    if (dotIndex !== -1) {
        result += '.' + decimalPart;
    }
    return result;
};

const AddProjectModal = ({ onClose, onAdd, project }) => {
    const isEdit = !!project;

    // Normalize status from backend (which might be COMPLETED or INPROGRESS) to match our select options
    const getInitialStatus = () => {
        if (!project?.status) return '';
        const s = project.status.toUpperCase();
        if (s === 'COMPLETED' || s === 'COMPLETE') return 'Completed';
        if (s === 'INPROGRESS' || s === 'IN_PROGRESS' || s === 'IN PROGRESS') return 'In Progress';
        return project.status;
    };

    const parsedInit = parseValueAndCurrency(project?.value);
    const [form, setForm] = useState({
        title: project?.title || '',
        client: project?.client || '',
        status: getInitialStatus(),
        currency: parsedInit.currency,
        amount: parsedInit.amount,
        completed_date: project?.completed_date || '',
        description: project?.description || '',
        document: null,
    });
    const [submitting, setSubmitting] = useState(false);
    const [apiError, setApiError] = useState('');
    const [formErrors, setFormErrors] = useState({});

    const validateDate = (dateStr) => {
        if (!dateStr) return true; // not required here — handled by required attr
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return false;
        const year = d.getFullYear();
        return year >= 1900 && year <= new Date().getFullYear();
    };

    useEffect(() => {
        // Lock body scrolling when modal opens
        document.body.style.overflow = 'hidden';
        return () => {
            // Restore body scrolling on unmount
            document.body.style.overflow = '';
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => {
            const nextForm = { ...prev, [name]: value };
            if (name === 'status' && value !== 'Completed') {
                nextForm.completed_date = '';
            }
            if (name === 'amount') {
                let clean = value.replace(/[^0-9.,bmkBMK]/g, '');
                const parts = clean.split('.');
                if (parts.length > 2) {
                    clean = parts[0] + '.' + parts.slice(1).join('');
                }
                const hasSuffix = /[bmkBMK]$/.test(clean);
                const suffix = hasSuffix ? clean.slice(-1) : '';
                const numberPart = hasSuffix ? clean.slice(0, -1) : clean;

                const raw = numberPart.replace(/,/g, '');
                nextForm.amount = formatValuation(raw, prev.currency) + suffix.toUpperCase();
            }
            if (name === 'currency') {
                const hasSuffix = /[bmkBMK]$/.test(prev.amount);
                const suffix = hasSuffix ? prev.amount.slice(-1) : '';
                const numberPart = hasSuffix ? prev.amount.slice(0, -1) : prev.amount;
                const raw = numberPart.replace(/,/g, '');
                nextForm.amount = formatValuation(raw, value) + suffix.toUpperCase();
            }
            return nextForm;
        });
        if (apiError) setApiError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = {};
        if (!form.title.trim()) errors.title = 'Proposal title is required.';
        if (!form.client.trim()) errors.client = 'Client name is required.';
        if (form.status === 'Completed' && !form.completed_date) {
            errors.completed_date = 'Completed date is required for Completed status.';
        }
        if (form.completed_date && !validateDate(form.completed_date)) {
            errors.completed_date = 'Please enter a valid date.';
        }
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        setFormErrors({});
        setSubmitting(true);
        setApiError('');

        const projectId = project?.project_id || project?.id || project?._id;
        const combinedValue = `${form.currency} ${form.amount}`.trim();

        try {
            let response;
            if (isEdit) {
                const payload = {
                    title: form.title.trim(),
                    client: form.client.trim() || 'Not Specified',
                    status: form.status || 'In Progress',
                    value: combinedValue || 'Not Specified',
                    completed_date: form.completed_date,
                    description: form.description.trim(),
                };
                response = await api.put(`/experience/${projectId}`, payload);
            } else {
                // Build the payload matching the API schema
                const payload = {
                    projects: [
                        {
                            title: form.title.trim(),
                            client: form.client.trim() || 'Not Specified',
                            status: form.status || 'In Progress',
                            value: combinedValue || 'Not Specified',
                            completed_date: form.completed_date,
                            description: form.description.trim(),
                        },
                    ],
                };
                response = await api.post('/experience', payload);
            }

            if (response.status === 200 || response.status === 201) {
                onAdd({
                    title: form.title,
                    client: form.client,
                    status: form.status || 'In Progress',
                    value: combinedValue,
                    completed_date: form.completed_date,
                    description: form.description,
                }, response.data?.message || (isEdit ? 'Project updated successfully' : 'Project added successfully'));
            } else {
                setApiError(response.data?.detail || `Failed to ${isEdit ? 'update' : 'add'} project. Please try again.`);
            }
        } catch (err) {
            console.error(`${isEdit ? 'Update' : 'Add'} project error:`, err);
            setApiError('Network error. Please check your connection.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="apm-overlay" onClick={onClose}>
            <div className="apm-container animate-apm-scale" onClick={(e) => e.stopPropagation()}>
                {/* Header Section */}
                <div className="apm-header">
                    <div className="apm-header-left">
                        <div className="apm-icon-badge">
                            {isEdit ? <Edit size={18} /> : <Briefcase size={18} />}
                        </div>
                        <div className="apm-header-text">
                            <h3>{isEdit ? 'Edit Company Project' : 'Add Company Project'}</h3>
                            <p>{isEdit ? 'Update details and status configurations' : 'Enter details to register a new project'}</p>
                        </div>
                    </div>
                    <button type="button" className="apm-close-btn" onClick={onClose} aria-label="Close modal">
                        <X size={18} />
                    </button>
                </div>

                <form className="apm-form" onSubmit={handleSubmit}>
                    <div className="apm-body">
                        <div className="apm-form-grid">
                            {/* Proposal Title */}
                            <div className="apm-form-field">
                                <label className="apm-label">Proposal title <span style={{color:'#dc2626'}}>*</span></label>
                                <input
                                    className="apm-input"
                                    type="text"
                                    name="title"
                                    placeholder="Enter project title..."
                                    value={form.title}
                                    onChange={handleChange}
                                    autoFocus
                                    required
                                />
                                {formErrors.title && <span style={{fontSize:'11px',color:'#dc2626'}}>{formErrors.title}</span>}
                            </div>

                            {/* Client & Project Value Row */}
                            <div className="apm-form-row">
                                <div className="apm-form-field">
                                    <label className="apm-label">Client <span style={{color:'#dc2626'}}>*</span></label>
                                    <input
                                        className="apm-input"
                                        type="text"
                                        name="client"
                                        placeholder="Client name..."
                                        value={form.client}
                                        onChange={handleChange}
                                        required
                                    />
                                    {formErrors.client && <span style={{fontSize:'11px',color:'#dc2626'}}>{formErrors.client}</span>}
                                </div>

                                <div className="apm-form-field">
                                    <label className="apm-label">Project value</label>
                                    <div className="valuation-composite-v3" style={{ marginTop: 0 }}>
                                        <div className="currency-box-v3">
                                            {/*
                                            <select 
                                                name="currency" 
                                                value={form.currency} 
                                                onChange={handleChange}
                                                disabled={form.amount?.toLowerCase() === 'confidential'}
                                                style={form.amount?.toLowerCase() === 'confidential' ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                            >
                                                <option value="AED">AED</option>
                                                <option value="USD">USD</option>
                                                <option value="EUR">EUR</option>
                                                <option value="SAR">SAR</option>
                                                <option value="IND">IND</option>
                                            </select>
                                            <ChevronDown size={12} className="select-arrow-v3" style={form.amount?.toLowerCase() === 'confidential' ? { opacity: 0.6 } : {}} />
                                            */}
                                            <span style={{ fontWeight: 600, fontSize: '13px', color: '#475569', padding: '0 8px' }}>AED</span>
                                        </div>
                                        <input
                                            type="text"
                                            name="amount"
                                            placeholder="e.g. 300,000"
                                            value={form.amount}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Status & Completed Date Row */}
                            <div className="apm-form-row">
                                <div className="apm-form-field">
                                    <label className="apm-label">Status</label>
                                    <select
                                        className="apm-input apm-select"
                                        name="status"
                                        value={form.status}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select status</option>
                                        <option value="Completed">Completed</option>
                                        <option value="In Progress">In Progress</option>
                                    </select>
                                </div>

                                <div className="apm-form-field">
                                    <label className="apm-label">Completed date {form.status === 'Completed' && <span style={{color:'#dc2626'}}>*</span>}</label>
                                    <input
                                        className={`apm-input apm-date-input${formErrors.completed_date ? ' apm-input-error' : ''}`}
                                        type="date"
                                        name="completed_date"
                                        value={form.completed_date}
                                        onChange={handleChange}
                                        min="1900-01-01"
                                        max={new Date().toISOString().split('T')[0]}
                                        disabled={form.status !== 'Completed'}
                                    />
                                    {formErrors.completed_date && <span style={{fontSize:'11px',color:'#dc2626'}}>{formErrors.completed_date}</span>}
                                </div>
                            </div>

                            <div className="apm-form-field">
                                <label className="apm-label">Description</label>
                                <textarea
                                    className="apm-input apm-textarea"
                                    name="description"
                                    placeholder="Brief project description..."
                                    value={form.description}
                                    onChange={handleChange}
                                    rows={3}
                                    style={{ fontFamily: 'inherit', fontWeight: 400 }}
                                />
                            </div>
                        </div>

                        {apiError && (
                            <div className="apm-api-error">{apiError}</div>
                        )}
                    </div>

                    {/* Footer Section */}
                    <div className="apm-footer">
                        <button type="button" className="apm-btn-cancel" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="apm-btn-add" disabled={submitting}>
                            {/* <Check size={16} /> */}
                            <span>{submitting ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Changes' : 'Add Project')}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddProjectModal;
